/**
 * ReferencePhotoSelector — 역할별 참조 사진 선택 + 인라인 업로드
 *
 * 선택한 역할에 따라 해당 역할의 참조 사진 목록을 보여주고 1장씩 선택.
 * 목록 끝에 '+' 버튼 → 프리뷰 Modal → 얼굴 감지 → 업로드 플로우.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { PersonRole, ReferencePhoto } from '@cugumi/shared/types'
import { Spinner, Modal, Button } from '@/components/ui'
import { apiFetch, getErrorMessage } from '@/lib/api'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/lib/cn'
import styles from './ReferencePhotoSelector.module.css'

interface ReferencePhotoSelectorProps {
  role: PersonRole
  groomPhotos: ReferencePhoto[]
  bridePhotos: ReferencePhoto[]
  selectedGroomId: string | null
  selectedBrideId: string | null
  onSelectGroom: (id: string) => void
  onSelectBride: (id: string) => void
  /** 업로드 콜백 (없으면 '+' 버튼 숨김) */
  onUpload?: (file: File, role: PersonRole) => Promise<void>
  /** 현재 업로드 중인 역할 */
  uploading?: PersonRole | null
}

type FaceDetectStatus = 'idle' | 'detecting' | 'ok' | 'error'

function PhotoRow({
  role,
  label,
  photos,
  selectedId,
  onSelect,
  onUpload,
  isUploading,
}: {
  role: 'GROOM' | 'BRIDE'
  label: string
  photos: ReferencePhoto[]
  selectedId: string | null
  onSelect: (id: string) => void
  onUpload?: (file: File, role: PersonRole) => Promise<void>
  isUploading: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const addToast = useUIStore((s) => s.addToast)

  // 업로드 프리뷰 상태
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [faceStatus, setFaceStatus] = useState<FaceDetectStatus>('idle')
  const [faceError, setFaceError] = useState<string | null>(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const previewUrlRef = useRef<string | null>(null)

  // 프리뷰 URL cleanup on unmount only
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [])

  const resetUploadPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setShowUploadModal(false)
    setPendingFile(null)
    setPreviewUrl(null)
    setFaceStatus('idle')
    setFaceError(null)
    setUploadingFile(false)
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
      const url = URL.createObjectURL(file)
      previewUrlRef.current = url
      setPendingFile(file)
      setPreviewUrl(url)
      setFaceStatus('idle')
      setFaceError(null)
    }
    e.target.value = ''
  }

  const handleFaceDetect = async () => {
    if (!pendingFile) return
    setFaceStatus('detecting')
    setFaceError(null)

    const formData = new FormData()
    formData.append('image', pendingFile)

    try {
      const result = await apiFetch<{ faceDetected: boolean }>(
        '/api/ai/face-detect',
        { method: 'POST', body: formData },
      )
      if (result.success) {
        setFaceStatus('ok')
      } else {
        setFaceStatus('error')
        setFaceError(getErrorMessage(result.error, '얼굴이 감지되지 않았습니다'))
      }
    } catch {
      setFaceStatus('error')
      setFaceError('얼굴 감지에 실패했습니다. 다시 시도해주세요')
    }
  }

  const handleUploadConfirm = async () => {
    if (!pendingFile || !onUpload) return
    setUploadingFile(true)
    try {
      await onUpload(pendingFile, role)
      addToast({ type: 'success', message: '참조 사진이 업로드되었습니다' })
      resetUploadPreview()
    } catch (err) {
      const msg = err instanceof Error ? err.message : '업로드에 실패했습니다'
      addToast({ type: 'error', message: msg })
      setFaceError(msg)
      setFaceStatus('error')
    } finally {
      setUploadingFile(false)
    }
  }

  return (
    <div className={styles.roleSection}>
      <span className={styles.roleLabel}>{label} 참조 사진</span>
      <div className={styles.photoGrid}>
        {photos.map((photo) => (
          <button
            key={photo.id}
            type="button"
            className={cn(styles.photoCard, selectedId === photo.id && styles.photoCardSelected)}
            onClick={() => onSelect(photo.id)}
          >
            <img src={photo.originalUrl} alt={label} className={styles.photoImage} />
            {selectedId === photo.id && <span className={styles.checkMark}>✓</span>}
          </button>
        ))}

        {onUpload && (
          <button
            type="button"
            className={styles.addCard}
            onClick={() => !isUploading && setShowUploadModal(true)}
            disabled={isUploading}
          >
            {isUploading ? <Spinner size="sm" /> : <span className={styles.addIcon}>+</span>}
          </button>
        )}
      </div>

      {photos.length === 0 && !onUpload && (
        <div className={styles.emptyMessage}>등록된 사진이 없습니다</div>
      )}
      {photos.length > 0 && !selectedId && (
        <span className={styles.selectHint}>사진을 선택해주세요</span>
      )}

      {/* 업로드 프리뷰 Modal */}
      <Modal
        open={showUploadModal}
        onClose={resetUploadPreview}
        title={`${label} 참조 사진 업로드`}
        size="md"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="업로드 프리뷰"
            className={styles.uploadPreviewImg}
          />
        ) : (
          <div className={styles.uploadPlaceholder} onClick={() => inputRef.current?.click()}>
            <span className={styles.uploadPlaceholderIcon}>+</span>
            <p className={styles.uploadPlaceholderHint}>클릭하여 사진 선택</p>
            <p className={styles.uploadPlaceholderHint}>JPG, PNG, WebP (최대 10MB)</p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={styles.hiddenInput}
          onChange={handleFileChange}
        />

        {faceStatus === 'detecting' && (
          <div className={styles.faceStatus}>
            <Spinner size="sm" /> 얼굴 감지 중...
          </div>
        )}

        {faceStatus === 'ok' && (
          <div className={`${styles.faceStatus} ${styles.faceStatusOk}`}>
            ✓ 얼굴이 감지되었습니다
          </div>
        )}

        {faceStatus === 'error' && (
          <div className={`${styles.faceStatus} ${styles.faceStatusError}`}>
            ✗ {faceError}
          </div>
        )}

        {pendingFile && (
          <div className={styles.uploadActions}>
            <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={faceStatus === 'detecting' || uploadingFile}>
              다른 사진
            </Button>

            {faceStatus === 'ok' ? (
              <Button size="sm" onClick={handleUploadConfirm} disabled={uploadingFile}>
                {uploadingFile ? <><Spinner size="sm" /> 업로드 중...</> : '업로드'}
              </Button>
            ) : (
              <Button size="sm" onClick={handleFaceDetect} disabled={faceStatus === 'detecting'}>
                {faceStatus === 'detecting' ? <><Spinner size="sm" /> 감지 중...</> : '얼굴 감지'}
              </Button>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export function ReferencePhotoSelector({
  role,
  groomPhotos,
  bridePhotos,
  selectedGroomId,
  selectedBrideId,
  onSelectGroom,
  onSelectBride,
  onUpload,
  uploading,
}: ReferencePhotoSelectorProps) {
  const showGroom = role === 'GROOM' || role === 'COUPLE'
  const showBride = role === 'BRIDE' || role === 'COUPLE'

  return (
    <div className={cn(styles.container, role === 'COUPLE' && styles.containerHorizontal)}>
      {showGroom && (
        <PhotoRow
          role="GROOM"
          label="신랑"
          photos={groomPhotos}
          selectedId={selectedGroomId}
          onSelect={onSelectGroom}
          onUpload={onUpload}
          isUploading={uploading === 'GROOM'}
        />
      )}
      {showBride && (
        <PhotoRow
          role="BRIDE"
          label="신부"
          photos={bridePhotos}
          selectedId={selectedBrideId}
          onSelect={onSelectBride}
          onUpload={onUpload}
          isUploading={uploading === 'BRIDE'}
        />
      )}
    </div>
  )
}
